import { COLLISION_TOLERANCE, COUPLING_GAP, PORT_OFFSETS } from './constants'
import { parsePort } from './keys'
import { getEdge } from './queries'
import type { Network, Vehicle } from './types'

export interface Point2D {
  x: number
  z: number
}

const posScratchA: Point2D = { x: 0, z: 0 }
const posScratchB: Point2D = { x: 0, z: 0 }

/**
 * Computes the 2D world position of a vehicle on its current edge.
 * Internal edges use a quadratic Bézier curve through the cell center.
 * External edges use linear interpolation between port openings.
 * Writes into `out` when provided to avoid allocating in hot paths.
 */
export function getVehiclePos(
  network: Network,
  vehicle: Vehicle,
  tOverride?: number,
  out?: Point2D,
): Point2D | null {
  const t = tOverride ?? vehicle.t

  const edge = getEdge(network, vehicle.edgeId)
  if (!edge) return null

  const pA = parsePort(edge.a)
  const pB = parsePort(edge.b)

  const u = edge.length === 0 ? t : t / edge.length
  const curveU = vehicle.direction === 1 ? u : 1 - u
  const result = out ?? { x: 0, z: 0 }

  if (edge.type === 'internal') {
    const cx = pA.x
    const cy = pA.y
    const startX = cx + PORT_OFFSETS[pA.direction].x
    const startZ = cy + PORT_OFFSETS[pA.direction].z
    const endX = cx + PORT_OFFSETS[pB.direction].x
    const endZ = cy + PORT_OFFSETS[pB.direction].z

    const mu = 1 - curveU
    result.x = mu * mu * startX + 2 * mu * curveU * cx + curveU * curveU * endX
    result.z = mu * mu * startZ + 2 * mu * curveU * cy + curveU * curveU * endZ
  } else {
    const startX = pA.x + PORT_OFFSETS[pA.direction].x
    const startZ = pA.y + PORT_OFFSETS[pA.direction].z
    const endX = pB.x + PORT_OFFSETS[pB.direction].x
    const endZ = pB.y + PORT_OFFSETS[pB.direction].z

    result.x = startX + (endX - startX) * curveU
    result.z = startZ + (endZ - startZ) * curveU
  }

  return result
}

/** Center-to-center distance at which two vehicles are considered touching. */
export function couplingThreshold(v1: Vehicle, v2: Vehicle): number {
  return (v1.length + v2.length) / 2 + COUPLING_GAP
}

/** Returns true if two vehicles are within coupling distance of each other. */
export function checkCollision(network: Network, v1: Vehicle, v2: Vehicle): boolean {
  const threshold = couplingThreshold(v1, v2)

  if (v1.edgeId === v2.edgeId) {
    const edge = getEdge(network, v1.edgeId)
    if (!edge) return false
    // Use arc-length-scaled track distance. Raw Euclidean fails on curved edges
    // (chord ~0.707 < threshold ~0.9 → always colliding). arcLength converts
    // t-units to world units so same-edge and cross-edge thresholds are consistent.
    // `t` is measured from the end the vehicle entered, so map both to the edge's a→b frame.
    const s1 = v1.direction === 1 ? v1.t : edge.length - v1.t
    const s2 = v2.direction === 1 ? v2.t : edge.length - v2.t
    return Math.abs(s1 - s2) * (edge.arcLength ?? 1) <= threshold + COLLISION_TOLERANCE
  }

  const pos1 = getVehiclePos(network, v1, undefined, posScratchA)
  const pos2 = getVehiclePos(network, v2, undefined, posScratchB)
  if (!pos1 || !pos2) return false

  const dx = pos1.x - pos2.x
  const dz = pos1.z - pos2.z
  const limit = threshold + COLLISION_TOLERANCE
  return dx * dx + dz * dz <= limit * limit
}

/** Deterministic key for an unordered vehicle pair. */
export function pairKey(aId: string, bId: string): string {
  return aId < bId ? `${aId}|${bId}` : `${bId}|${aId}`
}

/**
 * Returns the set of pair keys for all currently colliding vehicle pairs.
 * Vehicles in neighbouring cells sit on edges two hops apart (internal → external → internal),
 * so an edge-adjacency broad phase misses real contacts. Vehicle counts are small, so an
 * exhaustive pass is both exact and cheaper than building the grouping structures.
 */
export function getCollidingPairs(
  network: Network,
  vehicles: Vehicle[],
  targetPairs?: Set<string>,
): Set<string> {
  const pairs = targetPairs ?? new Set<string>()
  pairs.clear()

  for (let i = 0; i < vehicles.length; i++) {
    const a = vehicles[i]
    for (let j = i + 1; j < vehicles.length; j++) {
      const b = vehicles[j]
      if (checkCollision(network, a, b)) pairs.add(pairKey(a.id, b.id))
    }
  }

  return pairs
}

/**
 * Returns true if two colliding vehicles have free coupling sides and can link up.
 * Delegates side detection to the provided `getTouchingSide` function to avoid
 * circular dependency with coupling.ts.
 */
export function canVehiclesCouple(
  network: Network,
  vA: Vehicle,
  vB: Vehicle,
  getTouchingSide: (network: Network, v: Vehicle, target: Vehicle) => 'linkedFront' | 'linkedBack',
): boolean {
  if (!checkCollision(network, vA, vB)) return false

  const portA = getTouchingSide(network, vA, vB)
  const portB = getTouchingSide(network, vB, vA)
  return !vA[portA] && !vB[portB]
}
